// src/pages/fpo/FpoDashboard.js
// ✅ FULL READY-TO-PASTE (NO DELETION of your existing logic)
// ✅ FIX: Approve click → immediately shows in Approved tab (instant UI update)
// ✅ FIX: status comparisons normalized (Approved/APPROVED/approved)
// ✅ FIX: update BOTH sources when row comes from collectionGroup fallback (cg) OR subcollection
// ✅ FIX: cleanup listeners without storing unsub in state (cursor/loop safe)

import { useEffect, useMemo, useState, useRef } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../../firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  serverTimestamp,
  // ✅ FIX START: fallback collectionGroup query + downloads
  collectionGroup,
  where,
  // ✅ FIX END
} from "firebase/firestore";

const cx = (...xs) => xs.filter(Boolean).join(" ");

// ✅ helpers for premium stats + csv
const safeStr = (v) => String(v || "").trim();
const tsToMs = (t) => {
  if (!t) return 0;
  if (typeof t?.toMillis === "function") return t.toMillis();
  if (typeof t?.seconds === "number") return t.seconds * 1000;
  return 0;
};
const toCsvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const downloadCsv = ({ filename, header, rows }) => {
  const csv = [header.map(toCsvCell).join(","), ...rows.map((r) => r.map(toCsvCell).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ✅ status normalizer (critical)
const normStatus = (v) => String(v || "").trim().toLowerCase();
const rowKey = (r) => String(r?.memberDocId || r?.id || "").trim();

export default function FpoDashboard() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending"); // pending | approved
  const [search, setSearch] = useState("");

  // ✅ keep unsub refs so cleanup is correct (no state loop)
  const unsubSubRef = useRef(null);
  const unsubCgRef = useRef(null);
  const authUnsubRef = useRef(null);

  // premium stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    feeTotal: 0,
    lastAtMs: 0,
  });

  useEffect(() => {
    // cleanup old auth listener if any
    try { authUnsubRef.current?.(); } catch {}
    authUnsubRef.current = null;

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        window.location.href = "/fpo/login";
        return;
      }
      setUser(u);

      const snap = await getDoc(doc(db, "farmer_vendors", u.uid));
      if (!snap.exists()) {
        alert("Access denied: FPO profile not found.");
        await signOut(auth);
        window.location.href = "/fpo/login";
        return;
      }
      const data = snap.data() || {};
      if (String(data.status || "") !== "approved") {
        alert("Access denied: FPO not approved.");
        await signOut(auth);
        window.location.href = "/fpo/login";
        return;
      }
      setProfile({ id: snap.id, ...data });

      // cleanup previous listeners if any
      try { unsubSubRef.current?.(); } catch {}
      try { unsubCgRef.current?.(); } catch {}
      unsubSubRef.current = null;
      unsubCgRef.current = null;

      // ✅ live requests (subcollection)
      const qRef = query(
        collection(db, "farmer_vendors", u.uid, "member_requests"),
        orderBy("createdAt", "desc")
      );

      const unsub2 = onSnapshot(
        qRef,
        (s) => {
          const list = s.docs.map((d) => ({
            id: d.id,
            ...(d.data() || {}),
            __src: "subcol",
          }));

          // merge with any existing cg rows, dedupe by memberDocId
          setRows((prev) => {
            const prevCg = Array.isArray(prev) ? prev.filter((x) => x.__src === "cg") : [];
            const merged = [...list, ...prevCg];

            const seen = new Set();
            const out = [];
            for (const r of merged) {
              const key = rowKey(r);
              if (!key) continue;
              if (seen.has(key)) continue;
              seen.add(key);
              out.push(r);
            }

            out.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
            return out;
          });
        },
        (err) => {
          console.error("FPO member_requests snapshot error:", err);
        }
      );
      unsubSubRef.current = unsub2;

      // ✅ fallback collectionGroup: if requests written under different org doc path
      const qCg = query(
        collectionGroup(db, "member_requests"),
        where("linked_org_uid", "==", u.uid),
        orderBy("createdAt", "desc")
      );

      const unsubCg = onSnapshot(
        qCg,
        (s) => {
          const list = s.docs.map((d) => ({
            id: d.id,
            ...(d.data() || {}),
            __src: "cg",
          }));

          setRows((prev) => {
            const prevSub = Array.isArray(prev) ? prev.filter((x) => x.__src === "subcol") : [];
            const merged = [...prevSub, ...list];

            const seen = new Set();
            const out = [];
            for (const r of merged) {
              const key = rowKey(r);
              if (!key) continue;
              if (seen.has(key)) continue;
              seen.add(key);
              out.push(r);
            }

            out.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
            return out;
          });
        },
        (err) => {
          console.error("FPO member_requests collectionGroup snapshot error:", err);
        }
      );

      unsubCgRef.current = unsubCg;
    });

    authUnsubRef.current = unsub;

    return () => {
      try { authUnsubRef.current?.(); } catch {}
      try { unsubSubRef.current?.(); } catch {}
      try { unsubCgRef.current?.(); } catch {}
      authUnsubRef.current = null;
      unsubSubRef.current = null;
      unsubCgRef.current = null;
    };
  }, []);

  // compute premium stats from rows (normalized)
  useEffect(() => {
    const list = Array.isArray(rows) ? rows : [];
    let total = 0, approved = 0, pending = 0, feeTotal = 0, lastAtMs = 0;

    list.forEach((r) => {
      total += 1;
      const st = normStatus(r.status || "pending");
      if (st === "approved") approved += 1;
      else pending += 1;

      feeTotal += Number(r.membership_fee || 0) || 0;

      const ms = tsToMs(r.createdAt);
      if (ms > lastAtMs) lastAtMs = ms;
    });

    setStats({ total, pending, approved, feeTotal, lastAtMs });
  }, [rows]);

  // ✅ FIX: filtered list must normalize status
  const filtered = useMemo(() => {
    const q = String(search || "").toLowerCase().trim();
    const wantApproved = tab === "approved";

    return (rows || [])
      .filter((r) => {
        const st = normStatus(r.status || "pending");
        return wantApproved ? st === "approved" : st !== "approved";
      })
      .filter((r) => {
        if (!q) return true;
        const n = String(r.from_member_name || "").toLowerCase();
        const m = String(r.from_member_mobile || "").toLowerCase();
        const e = String(r.from_member_email || "").toLowerCase();
        return n.includes(q) || m.includes(q) || e.includes(q);
      });
  }, [rows, tab, search]);

  /* =========================
     ✅ APPROVE: update Firestore + instant UI + try both ids
     - if row came from subcol: update doc(...reqRow.id)
     - if row came from cg: try reqRow.memberDocId first then reqRow.id
  ========================= */
  const approveRequest = async (reqRow) => {
    if (!user) return;

    const k = rowKey(reqRow);
    if (!k) return alert("Missing request id.");

    // optimistic UI update so it instantly appears in Approved tab
    setRows((prev) =>
      (prev || []).map((x) => (rowKey(x) === k ? { ...x, status: "approved" } : x))
    );

    try {
      // 1) try update by subcollection doc id first (reqRow.id)
      await updateDoc(
        doc(db, "farmer_vendors", user.uid, "member_requests", reqRow.id),
        { status: "approved", decidedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      );

      alert("✅ Approved in FPO portal.");
      return;
    } catch (e1) {
      // continue fallback
      console.warn("approveRequest primary update failed:", e1?.message || e1);
    }

    try {
      // 2) fallback update by memberDocId (some setups used memberDocId as docId)
      if (reqRow.memberDocId) {
        await updateDoc(
          doc(db, "farmer_vendors", user.uid, "member_requests", reqRow.memberDocId),
          { status: "approved", decidedAt: serverTimestamp(), updatedAt: serverTimestamp() }
        );
        alert("✅ Approved in FPO portal.");
        return;
      }
    } catch (e2) {
      console.warn("approveRequest fallback update failed:", e2?.message || e2);
    }

    // rollback UI if both failed
    setRows((prev) =>
      (prev || []).map((x) => (rowKey(x) === k ? { ...x, status: reqRow.status || "pending" } : x))
    );

    alert("❌ Approve failed (permission or wrong doc id). Please contact admin.");
  };

  // CSV downloads (current tab filtered / all)
  const downloadFilteredCsv = () => {
    const list = Array.isArray(filtered) ? filtered : [];
    const header = [
      "MemberDocId",
      "Name",
      "Mobile",
      "Email",
      "Membership Type",
      "Farmer Category",
      "Fee",
      "Status",
      "CreatedAt",
      "Linked Org UID",
      "Linked Org Name",
      "Source",
    ];

    const rowsCsv = list.map((r) => [
      r.memberDocId || r.id || "",
      r.from_member_name || "",
      r.from_member_mobile || "",
      r.from_member_email || "",
      r.membership_type || "",
      r.farmer_category || "",
      Number(r.membership_fee || 0) || 0,
      String(r.status || "pending"),
      tsToMs(r.createdAt) ? new Date(tsToMs(r.createdAt)).toLocaleString() : "",
      r.linked_org_uid || user?.uid || "",
      r.linked_org_name || (profile?.name || profile?.company_name || profile?.org_name || ""),
      r.__src || "",
    ]);

    const orgName = profile?.name || profile?.company_name || profile?.org_name || "Organization";
    const fileName = `FPO_${String(orgName).replace(/[^\w\-]+/g, "_")}_${tab}_requests.csv`;

    downloadCsv({ filename: fileName, header, rows: rowsCsv });
  };

  const downloadAllCsv = () => {
    const list = Array.isArray(rows) ? rows : [];
    const header = [
      "MemberDocId",
      "Name",
      "Mobile",
      "Email",
      "Membership Type",
      "Farmer Category",
      "Fee",
      "Status",
      "CreatedAt",
      "Linked Org UID",
      "Linked Org Name",
      "Source",
    ];

    const rowsCsv = list.map((r) => [
      r.memberDocId || r.id || "",
      r.from_member_name || "",
      r.from_member_mobile || "",
      r.from_member_email || "",
      r.membership_type || "",
      r.farmer_category || "",
      Number(r.membership_fee || 0) || 0,
      String(r.status || "pending"),
      tsToMs(r.createdAt) ? new Date(tsToMs(r.createdAt)).toLocaleString() : "",
      r.linked_org_uid || user?.uid || "",
      r.linked_org_name || (profile?.name || profile?.company_name || profile?.org_name || ""),
      r.__src || "",
    ]);

    const orgName = profile?.name || profile?.company_name || profile?.org_name || "Organization";
    const fileName = `FPO_${String(orgName).replace(/[^\w\-]+/g, "_")}_ALL_requests.csv`;

    downloadCsv({ filename: fileName, header, rows: rowsCsv });
  };

  const logout = async () => {
    await signOut(auth);
    window.location.href = "/fpo/login";
  };

  const orgTitle = profile?.name || profile?.company_name || profile?.org_name || "Organization";
  const lastAtText = stats.lastAtMs ? new Date(stats.lastAtMs).toLocaleString() : "-";

  return (
    <div className="min-h-screen bg-[radial-gradient(1200px_520px_at_50%_-120px,#dbeafe_0%,#f8fafc_45%,#ffffff_100%)]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          <div className="px-6 py-5 bg-gradient-to-r from-blue-950 via-slate-900 to-emerald-950 text-white">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-white/75">
                  FPO / Cooperative Portal
                </div>
                <div className="mt-1 text-[18px] md:text-[22px] font-extrabold leading-snug">
                  {orgTitle}
                </div>
                <div className="mt-1 text-[11px] text-white/80">
                  Logged in as: <b className="text-white">{user?.email || "-"}</b>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={downloadFilteredCsv}
                  className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-[11px] font-extrabold hover:bg-white/15"
                >
                  ⬇ Download Current List (CSV)
                </button>
                <button
                  onClick={downloadAllCsv}
                  className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-[11px] font-extrabold hover:bg-white/15"
                >
                  ⬇ Download ALL (CSV)
                </button>
                <button
                  onClick={logout}
                  className="px-4 py-2 rounded-full bg-rose-500/20 border border-rose-200/30 text-rose-100 text-[11px] font-extrabold hover:bg-rose-500/30"
                >
                  Logout
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold">Total Farmers</div>
                <div className="mt-1 text-xl font-extrabold">{stats.total}</div>
                <div className="text-[10px] text-white/70 mt-1">All requests</div>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold">Pending</div>
                <div className="mt-1 text-xl font-extrabold">{stats.pending}</div>
                <div className="text-[10px] text-white/70 mt-1">Need action</div>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold">Approved</div>
                <div className="mt-1 text-xl font-extrabold">{stats.approved}</div>
                <div className="text-[10px] text-white/70 mt-1">Accepted</div>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold">Total Fee (₹)</div>
                <div className="mt-1 text-xl font-extrabold">
                  {Number(stats.feeTotal || 0).toLocaleString("en-IN")}
                </div>
                <div className="text-[10px] text-white/70 mt-1">Sum of fees</div>
              </div>
              <div className="rounded-2xl bg-white/10 border border-white/10 p-4">
                <div className="text-[10px] text-white/70 uppercase tracking-widest font-bold">Last Registration</div>
                <div className="mt-1 text-[12px] font-extrabold">{lastAtText}</div>
                <div className="text-[10px] text-white/70 mt-1">Latest activity</div>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setTab("pending")}
                  className={cx(
                    "px-4 py-2 rounded-full text-[12px] font-extrabold border transition",
                    tab === "pending"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  )}
                >
                  Pending
                </button>
                <button
                  onClick={() => setTab("approved")}
                  className={cx(
                    "px-4 py-2 rounded-full text-[12px] font-extrabold border transition",
                    tab === "approved"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  )}
                >
                  Approved
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / mobile / email..."
                  className="w-full md:w-96 border border-slate-300 rounded-xl px-4 py-2.5 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-sm font-extrabold text-slate-900 flex items-center justify-between">
                <div>Requests</div>
                <div className="text-[11px] text-slate-500 font-semibold">
                  Showing: <b>{filtered.length}</b> • Tab: <b>{tab.toUpperCase()}</b>
                </div>
              </div>

              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white border-b border-slate-200">
                    <tr className="text-left">
                      <th className="p-3">Name</th>
                      <th className="p-3">Mobile</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Fee</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => {
                      const isApproved = normStatus(r.status || "pending") === "approved";
                      return (
                        <tr key={rowKey(r) || r.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 font-semibold text-slate-900">{r.from_member_name || "-"}</td>
                          <td className="p-3">{r.from_member_mobile || "-"}</td>
                          <td className="p-3">{r.from_member_email || "-"}</td>
                          <td className="p-3">₹{Number(r.membership_fee || 0).toLocaleString("en-IN")}</td>
                          <td className="p-3">
                            <span
                              className={cx(
                                "px-2 py-1 rounded-full text-xs font-extrabold border",
                                isApproved
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"
                              )}
                            >
                              {String(r.status || "pending").trim().toUpperCase()}
                            </span>
                          </td>
                          <td className="p-3">
                            {isApproved ? (
                              <span className="text-xs text-slate-500">Approved</span>
                            ) : (
                              <button
                                onClick={() => approveRequest(r)}
                                className="px-3 py-1.5 rounded-full bg-emerald-600 text-white font-extrabold hover:bg-emerald-700"
                              >
                                Approve
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {filtered.length === 0 && (
                      <tr>
                        <td className="p-4 text-slate-500" colSpan={6}>
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[11px] text-slate-500 mt-4">
              Note: Reject option intentionally disabled (as per your instruction).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
